import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Crown,
  Zap,
  Loader2,
  ExternalLink,
  AlertCircle,
  Calendar,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import {
  redirectToCheckout,
  redirectToPortal,
} from "../../services/stripeService";
import { getUserSubscription } from "../../services/subscriptionService";
import { deleteUserAccount } from "../../services/supabaseAuth";
import { logoutUser } from "../../app/features/auth";
import Navigation from "../../components/sections/Navigation";
import Button from "../../components/ui/Button";
import toast from "react-hot-toast";
import { Footer } from "../../components";

const Subscription = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadSubscription = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { subscription: sub, error } = await getUserSubscription(user.id);
      if (error) {
        console.error("Error loading subscription:", error);
        toast.error("Failed to load subscription");
      } else {
        setSubscription(sub);
      }
    } catch (error) {
      console.error("Exception loading subscription:", error);
      toast.error("Failed to load subscription");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    document.documentElement.classList.add("dark");
    if (user?.id) {
      loadSubscription();
    } else {
      setLoading(false);
    }
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, [user, loadSubscription]);

  const handleUpgrade = async (planType) => {
    if (!user?.id) {
      toast.error("Please sign in to upgrade");
      return;
    }

    const priceId =
      planType === "plan_c"
        ? import.meta.env.REACT_APP_STRIPE_PLAN_C_PRICE_ID
        : import.meta.env.REACT_APP_STRIPE_PLAN_B_PRICE_ID;

    if (!priceId) {
      toast.error("Stripe configuration error. Please contact support.");
      return;
    }

    setProcessing(true);
    try {
      await redirectToCheckout(priceId, user.id);
      // User will be redirected to Stripe
    } catch (error) {
      console.error("Error redirecting to checkout:", error);
      toast.error(
        error?.message || "Failed to start checkout. Please try again."
      );
      setProcessing(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!subscription?.stripe_customer_id) {
      toast.error("No subscription found");
      return;
    }

    setProcessing(true);
    try {
      await redirectToPortal(subscription.stripe_customer_id);
      // User will be redirected to Stripe portal
    } catch (error) {
      console.error("Error redirecting to portal:", error);
      toast.error(
        error?.message || "Failed to open customer portal. Please try again."
      );
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleDeleteAccount = async () => {
    if (!user?.id) {
      toast.error("No user found");
      return;
    }

    setDeleting(true);
    try {
      const { error } = await deleteUserAccount(user.id);

      if (error) {
        toast.error(error.message || "Failed to delete account");
        setDeleting(false);
        setShowDeleteConfirm(false);
        return;
      }

      toast.success("Account deleted successfully");
      // Dispatch logout to clear Redux state
      await dispatch(logoutUser());
      // Navigate to home page
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("An error occurred while deleting your account");
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getPlanDetails = (planType) => {
    switch (planType) {
      case "plan_c":
        return {
          name: "Pro",
          price: "$10/month",
          limit: "Unlimited",
          icon: <Crown className="w-6 h-6" />,
          color: "text-rose-400",
          bgColor: "bg-rose-500/20",
        };
      case "plan_b":
        return {
          name: "Basic",
          price: "$4/month",
          limit: "5 per 24 hours",
          icon: <Crown className="w-6 h-6" />,
          color: "text-teal-400",
          bgColor: "bg-teal-500/20",
        };
      default:
        return {
          name: "Free",
          price: "Free",
          limit: "1 per 24 hours",
          icon: <Zap className="w-6 h-6" />,
          color: "text-zinc-400",
          bgColor: "bg-zinc-800",
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 text-zinc-100 font-sans">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-stone-950 text-zinc-100 font-sans">
        <Navigation />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <AlertCircle className="w-12 h-12 text-zinc-400 mb-4" />
          <h2 className="text-2xl font-bold mb-2">Sign In Required</h2>
          <p className="text-zinc-400 mb-6">
            Please sign in to view your subscription
          </p>
          <Button onClick={() => navigate("/login")}>Sign In</Button>
        </div>
      </div>
    );
  }

  const planDetails = getPlanDetails(subscription?.plan_type || "free");
  const isActive =
    subscription?.status === "active" || subscription?.status === "trialing";
  const isFree = !subscription || subscription.plan_type === "free";

  return (
    <div className="min-h-screen bg-stone-950 text-zinc-100 font-sans">
      <Navigation />

      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto relative">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-zinc-100 via-teal-100 to-rose-100 bg-clip-text text-transparent">
              Subscription Management
            </h1>
            <p className="text-lg text-zinc-400">
              Manage your subscription and billing
            </p>
          </div>

          {/* Current Plan Card */}
          <div className="rounded-2xl p-6 sm:p-8 border border-zinc-800 bg-stone-900/30 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${planDetails.bgColor}`}>
                  <div className={planDetails.color}>{planDetails.icon}</div>
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{planDetails.name}</h2>
                  <p className="text-zinc-400">{planDetails.price}</p>
                </div>
              </div>
              <div
                className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  isActive
                    ? "bg-teal-500/20 text-teal-400 border border-teal-500/30"
                    : "bg-zinc-800 text-zinc-400 border border-zinc-700"
                }`}
              >
                {subscription?.status === "active"
                  ? "Active"
                  : subscription?.status === "trialing"
                  ? "Trialing"
                  : subscription?.status === "canceled"
                  ? "Canceled"
                  : "Free"}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-zinc-900/50">
                <Zap className="w-5 h-5 text-teal-400" />
                <div>
                  <p className="text-sm text-zinc-400">Daily Limit</p>
                  <p className="font-semibold">{planDetails.limit}</p>
                </div>
              </div>
              {subscription?.current_period_end && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-zinc-900/50">
                  <Calendar className="w-5 h-5 text-rose-400" />
                  <div>
                    <p className="text-sm text-zinc-400">
                      {subscription.cancel_at_period_end ? "Expires" : "Renews"}
                    </p>
                    <p className="font-semibold">
                      {formatDate(subscription.current_period_end)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {!isFree && (
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleManageSubscription}
                  disabled={processing}
                  className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Manage Subscription
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Upgrade Options */}
          {isFree && (
            <div className="rounded-2xl p-6 sm:p-8 border border-zinc-800 bg-stone-900/30 mb-6">
              <h3 className="text-xl font-bold mb-4">Upgrade Your Plan</h3>
              <p className="text-zinc-400 mb-6">
                Get more analyses and unlock premium features
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/30">
                  <div className="flex items-center gap-3 mb-3">
                    <Crown className="w-5 h-5 text-teal-400" />
                    <h4 className="font-semibold">Basic</h4>
                  </div>
                  <p className="text-2xl font-bold mb-2">$4/month</p>
                  <p className="text-sm text-zinc-400 mb-4">
                    5 analyses per 24 hours
                  </p>
                  <Button
                    onClick={() => handleUpgrade("plan_b")}
                    disabled={processing}
                    className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      "Upgrade to Basic"
                    )}
                  </Button>
                </div>

                <div className="p-4 rounded-lg border border-teal-500/50 bg-teal-950/30">
                  <div className="flex items-center gap-3 mb-3">
                    <Crown className="w-5 h-5 text-rose-400" />
                    <h4 className="font-semibold">Pro</h4>
                    <span className="text-xs px-2 py-1 rounded-full bg-teal-500/20 text-teal-400">
                      Popular
                    </span>
                  </div>
                  <p className="text-2xl font-bold mb-2">$10/month</p>
                  <p className="text-sm text-zinc-400 mb-4">
                    Unlimited analyses
                  </p>
                  <Button
                    onClick={() => handleUpgrade("plan_c")}
                    disabled={processing}
                    className="w-full bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      "Upgrade to Pro"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {subscription?.plan_type === "plan_b" && (
            <div className="rounded-2xl p-6 sm:p-8 border border-zinc-800 bg-stone-900/30">
              <h3 className="text-xl font-bold mb-4">Upgrade to Pro</h3>
              <p className="text-zinc-400 mb-6">
                Get unlimited analyses for just $6 more per month
              </p>
              <Button
                onClick={() => handleUpgrade("plan_c")}
                disabled={processing}
                className="w-full sm:w-auto bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Upgrade to Pro"
                )}
              </Button>
            </div>
          )}

          {/* Info */}
          <div className="mt-6 text-center">
            <p className="text-sm text-zinc-400">
              Need help? Contact our support team or visit our{" "}
              <button
                onClick={() => navigate("/pricing")}
                className="text-teal-400 hover:text-teal-300 underline"
              >
                pricing page
              </button>
            </p>
          </div>

          {/* Delete Account Section */}
          <div className="mt-12 pt-8 border-t border-zinc-800">
            <div className="rounded-2xl p-6 border border-red-500/30 bg-red-950/10">
              <h3 className="text-xl font-bold mb-2 text-red-400">
                Danger Zone
              </h3>
              <p className="text-sm text-zinc-400 mb-4">
                Once you delete your account, there is no going back. This will
                permanently delete your account and all associated data.
              </p>

              {!showDeleteConfirm ? (
                <Button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Delete My Account
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                    <p className="text-sm text-red-400 font-semibold mb-2">
                      Are you absolutely sure?
                    </p>
                    <p className="text-xs text-zinc-400">
                      This action cannot be undone. This will permanently delete
                      your account, subscription, and all your data.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 mr-2" />
                          Yes, Delete My Account
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleting}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Subscription;
